import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * tailwind-merge has to be told about the design system's custom scales.
 *
 * It resolves conflicts by *recognising* class names, and it only ships
 * knowledge of stock Tailwind. Given an unfamiliar `text-*` it guesses
 * "text colour", so `cn("text-white", "text-control")` decided the two
 * conflicted and kept the later one — which silently stripped `text-white` off
 * every primary button and rendered ink-on-steel. Same shape of bug is latent
 * on the radius and shadow scales, where the opposite happens: two role classes
 * are not recognised as conflicting, both survive, and which one wins comes
 * down to stylesheet order.
 *
 * Keep these lists in step with the `@theme` block in `index.css`. A token
 * added there and missed here does not fail loudly — it fails like a black
 * button.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      // Type ROLES, not a size ramp — see index.css.
      "font-size": [
        {
          text: [
            "page-title",
            "page-title-sm",
            "section-title",
            "figure-xl",
            "figure-lg",
            "figure-md",
            "figure-sm",
            "card-title",
            "card-title-sm",
            "lead",
            "body",
            "control",
            "card-body",
            "meta",
            "fine",
            "micro",
            "chip",
            "label",
            "label-sm",
          ],
        },
      ],
      // Role-based radii: card 14, panel 12, button 10, control 9, and so on.
      rounded: [
        {
          rounded: [
            "tooltip",
            "checkbox",
            "control",
            "button",
            "nav",
            "avatar-square",
            "avatar-square-lg",
            "panel",
            "card",
            "pill",
          ],
        },
      ],
      // Ink- and steel-cast shadows with negative spread.
      shadow: [
        {
          shadow: [
            "card-rest",
            "list-hover",
            "portrait-hover",
            "button-primary",
            "button-on-image",
            "ai-hover",
            "menu",
            "tooltip",
            "frame",
            "steel",
          ],
        },
      ],
      // The eight named animations.
      animate: [
        {
          animate: [
            "rise-in",
            "fade-in",
            "pop-in",
            "shimmer",
            "sweep",
            "bar-grow",
            "drift",
            "caret",
          ],
        },
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
